import { useNavigate } from '@tanstack/react-router';
import { addDays, addWeeks, format, startOfWeek } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { type ReactNode, useMemo } from 'react';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import { useTimezone } from '@/components/contexts/TimezoneContext';
import { Loading } from '@/components/shared';
import { normalizeTaskState } from '@/components/tasks/constants';
import { Button } from '@/components/ui/button';
import type { HomeDeadline } from '@/hooks/useHomeDashboard';
import { getEnvConfig } from '@/lib/env-config';
import { type RegionalDateLocale, weekStartsOnForLocale } from '@/utils/date-utils';

export const DAY_COUNT = 7;
export const WEEK_COUNT = 4;

function deploymentDateLocale(): RegionalDateLocale {
  try {
    return getEnvConfig().environment === 'productionus' ? 'us' : 'uk';
  } catch {
    return 'uk';
  }
}

export function homeWeekStartsOn(
  region?: string | null,
  fallbackLocale: RegionalDateLocale = 'uk',
): 0 | 1 {
  if (region) {
    return weekStartsOnForLocale(region.toUpperCase() === 'US' ? 'us' : 'uk');
  }
  return weekStartsOnForLocale(fallbackLocale);
}

export function useHomeWeekStartsOn(regionOverride?: string | null): 0 | 1 {
  const { matterRegion } = useMatterJurisdiction();
  return homeWeekStartsOn(regionOverride ?? matterRegion, deploymentDateLocale());
}


export function startOfToday(now = new Date(), timezone?: string) {
  const zoned = timezone ? toZonedTime(now, timezone) : now;
  return new Date(zoned.getFullYear(), zoned.getMonth(), zoned.getDate());
}

export function dateKey(date: Date) {
  return format(date, 'yyyy-MM-dd');
}

export function weekRangeLabel(start: Date, end: Date) {
  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
  }
  if (start.getMonth() === end.getMonth()) {
    return `${format(start, 'd')}–${format(end, 'd MMMM')}`;
  }
  return `${format(start, 'd MMM')} – ${format(end, 'd MMM')}`;
}

export function weekTabLabel(offset: number, start: Date) {
  if (offset === 0) return 'This week';
  if (offset === 1) return 'Next';
  return format(start, 'd MMM');
}

export function stateLabel(state: string) {
  const normalized = normalizeTaskState(state);
  if (normalized === 'Extracted') return 'Provisional';
  return normalized;
}


export type WeekSection = {
  offset: number;
  start: Date;
  end: Date;
  deadlines: HomeDeadline[];
};

export function buildWeeks(
  source: HomeDeadline[],
  today: Date,
  weekStartsOn: 0 | 1 = 1,
): WeekSection[] {
  const weekStart = startOfWeek(today, { weekStartsOn });
  return Array.from({ length: WEEK_COUNT }, (_, offset) => {
    const start = addWeeks(weekStart, offset);
    const end = addDays(start, DAY_COUNT - 1);
    const keys = new Set(
      Array.from({ length: DAY_COUNT }, (_, index) => dateKey(addDays(start, index)))
    );
    const todayKey = dateKey(today);
    return {
      offset,
      start,
      end,
      deadlines: source
        .filter((deadline) => {
          if (!deadline.date || !keys.has(deadline.date)) return false;
          // "This week" is forward-looking: hide calendar days before today.
          if (offset === 0 && deadline.date < todayKey) return false;
          return true;
        })
        .sort((a, b) => a.date.localeCompare(b.date) || a.description.localeCompare(b.description)),
    };
  });
}

export function firstNonEmptyWeekOffset(weeks: WeekSection[]): number {
  return weeks.find((week) => week.deadlines.length > 0)?.offset ?? 0;
}

export type DeadlineExploreProps = {
  deadlines: HomeDeadline[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  className?: string;
  /** Region for week-start (US = Sunday). Prefer the top-left / selected matter. */
  matterRegion?: string | null;
  /** Configured timezone; week buckets must match the dashboard response. */
  timezone?: string;
};

export function useExploreDeadlines({
  deadlines,
  matterRegion,
  timezone,
}: Pick<DeadlineExploreProps, 'deadlines' | 'matterRegion' | 'timezone'>) {
  const navigate = useNavigate();
  const { timezone: contextTimezone } = useTimezone();
  const weekTimezone = timezone ?? contextTimezone;
  // Key the cutoff by the zoned calendar day so a render after midnight moves it.
  const todayKey = dateKey(startOfToday(new Date(), weekTimezone));
  const today = useMemo(() => {
    const [year, month, day] = todayKey.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, [todayKey]);
  const weekStartsOn = useHomeWeekStartsOn(matterRegion);
  const weeks = useMemo(
    () => buildWeeks(deadlines, today, weekStartsOn),
    [deadlines, today, weekStartsOn],
  );

  const openDeadline = (deadline: HomeDeadline) => {
    navigate({
      to: '/matter/$slug/deadlines',
      params: { slug: deadline.matter_guid },
      search: { taskId: deadline.task_guid },
    });
  };

  return { today, weeks, openDeadline };
}

export function DeadlineWidgetStatus({
  isLoading,
  isError,
  onRetry,
  message = "Couldn't load deadlines.",
  children,
}: {
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  message?: string;
  children: ReactNode;
}) {
  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-8">
        <Loading />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-6 text-center">
        <p className="text-sm text-muted-foreground">{message}</p>
        {onRetry && (
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
      </div>
    );
  }
  return <>{children}</>;
}
